import { api } from './client';
import type { UnitOfMeasure, VatRate } from './catalogs';

export type NomenclatureKind = 'GOODS' | 'SERVICE' | 'WORK' | 'PROPERTY_RIGHT';

export const NOMENCLATURE_KIND_LABELS: Record<NomenclatureKind, string> = {
  GOODS: 'Товар',
  SERVICE: 'Услуга',
  WORK: 'Работа',
  PROPERTY_RIGHT: 'Имущественное право',
};

export interface NomenclatureGroup {
  id: string;
  name: string;
  parentId: string | null;
}

export interface NomenclatureGroupInput {
  name: string;
  parentId?: string | null;
}

export interface Nomenclature {
  id: string;
  article: string | null;
  name: string;
  fullName: string | null;
  kind: NomenclatureKind;
  unitId: string;
  unit: UnitOfMeasure;
  vatRateId: string | null;
  vatRate: VatRate | null;
  price: string | null;
  tnvedCode: string | null;
  countryCode: string | null;
  isTraceable: boolean;
  groupId: string | null;
  group: NomenclatureGroup | null;
  isActive: boolean;
}

export interface NomenclatureInput {
  article?: string | null;
  name: string;
  fullName?: string | null;
  kind: NomenclatureKind;
  unitId: string;
  vatRateId?: string | null;
  price?: string | null;
  tnvedCode?: string | null;
  countryCode?: string | null;
  isTraceable?: boolean;
  groupId?: string | null;
}

export interface NomenclatureListResponse {
  items: Nomenclature[];
  total: number;
  page: number;
  pageSize: number;
}

export interface NomenclatureListParams {
  search?: string;
  groupId?: string;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: NomenclatureListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.groupId) search.set('groupId', params.groupId);
  if (params.includeInactive) search.set('includeInactive', 'true');
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const nomenclatureApi = {
  list: (params: NomenclatureListParams = {}) =>
    api.get<NomenclatureListResponse>(`/nomenclature${buildQuery(params)}`),

  get: (id: string) => api.get<{ item: Nomenclature }>(`/nomenclature/${id}`),

  create: (data: NomenclatureInput) => api.post<{ item: Nomenclature }>('/nomenclature', data),

  update: (id: string, data: Partial<NomenclatureInput>) =>
    api.patch<{ item: Nomenclature }>(`/nomenclature/${id}`, data),

  remove: (id: string) => api.delete<void>(`/nomenclature/${id}`),

  activate: (id: string) => api.post<{ item: Nomenclature }>(`/nomenclature/${id}/activate`),

  deactivate: (id: string) => api.post<{ item: Nomenclature }>(`/nomenclature/${id}/deactivate`),
};

export const nomenclatureGroupsApi = {
  list: () => api.get<{ items: NomenclatureGroup[] }>('/nomenclature-groups'),

  create: (data: NomenclatureGroupInput) =>
    api.post<{ group: NomenclatureGroup }>('/nomenclature-groups', data),

  update: (id: string, data: Partial<NomenclatureGroupInput>) =>
    api.patch<{ group: NomenclatureGroup }>(`/nomenclature-groups/${id}`, data),

  remove: (id: string) => api.delete<void>(`/nomenclature-groups/${id}`),
};
